import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DisclaimerFooter from './DisclaimerFooter';
import SEO from './SEO';
import { ArrowDownTrayIcon, XMarkIcon, TableCellsIcon, StarIcon, MagnifyingGlassIcon, ChartBarIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import { ChevronUpDownIcon, Squares2X2Icon, ArrowsUpDownIcon } from '@heroicons/react/20/solid';
import { generateDataTablePDF, formatPDFNumber, formatPDFPercentage } from '../utils/pdfExport';
import { useAuth } from '../context/AuthContext';
import PdfExportModal from './PdfExportModal';
import { getWhitelabelSettings } from '../utils/whitelabelPdf';

const QUICK_LINKS = [
  {
    label: 'Power List™',
    description: 'Proprietary stock rankings scored with alternative data signals.',
    href: '/rankings/power-list',
    icon: StarIcon,
    accent: 'text-accent-100',
    border: 'hover:border-accent-100',
  },
  {
    label: 'Analyze',
    description: 'Build and compare watchlists with factor and sector attribution.',
    href: '/analyze',
    icon: ChartBarIcon,
    accent: 'text-primary-100',
    border: 'hover:border-primary-100',
  },
  {
    label: 'Research',
    description: 'Deep-dive into any ticker or ETF with institutional-grade data.',
    href: '/research',
    icon: MagnifyingGlassIcon,
    accent: 'text-signal-positive',
    border: 'hover:border-signal-positive',
  },
  {
    label: 'Learn',
    description: 'Methodology, quant factors, and platform guides.',
    href: '/learn',
    icon: BookOpenIcon,
    accent: 'text-text-200',
    border: 'hover:border-bg-300',
  },
];

const SORT_OPTIONS = [
  { key: 'ann_ret', label: 'Annual Return' },
  { key: 'sharpe', label: 'Sharpe Ratio' },
  { key: 'excess_ret', label: 'Excess Return' },
  { key: 'no_stocks', label: 'Holdings' },
  { key: 'model_name', label: 'Name' },
];

const TABLE_COLUMNS = [
  { key: 'ann_ret', label: 'Annual Return (%)' },
  { key: 'sharpe', label: 'Sharpe Ratio' },
  { key: 'excess_ret', label: 'Excess Return (%)' },
  { key: 'month_turnover', label: 'Monthly Turnover (%)' },
];

function getHoldingsCount(model) {
  return model.no_stocks || model.num_etfs || 0;
}

function getUniverse(model) {
  return model.universe || (model.model_type === 'etf' ? 'Sector Rotation' : '—');
}

function getWhitelabelModelName(modelName) {
  if (!modelName) return 'N/A';
  return modelName.toLowerCase().startsWith('sharpemetrix ')
    ? modelName.slice('SharpeMetrix '.length)
    : modelName;
}

export default function ModelsTableDashboard() {
  const { userRecord } = useAuth();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState('no_stocks');
  const [sortDir, setSortDir] = useState('asc');
  const [typeFilter, setTypeFilter] = useState('all');
  const [universeFilter, setUniverseFilter] = useState('all');
  const [holdingsFilter, setHoldingsFilter] = useState('all');
  const [viewMode, setViewMode] = useState('cards');
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    const fetchModels = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/models`);
        if (isMounted) setModels(response.data);
      } catch (err) {
        console.error('There was an error fetching the models!', err);
        if (isMounted) setError('Failed to load models. Please try again later.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchModels();
    return () => { isMounted = false; };
  }, []);

  const universes = useMemo(() => {
    const set = new Set(models.map(m => getUniverse(m)).filter(Boolean));
    return Array.from(set).sort();
  }, [models]);

  const holdingsRanges = useMemo(() => {
    const counts = models.map(m => getHoldingsCount(m)).filter(Boolean);
    if (counts.length === 0) return [];
    const ranges = [];
    if (counts.some(c => c <= 10)) ranges.push({ value: '1-10', label: '1–10', min: 1, max: 10 });
    if (counts.some(c => c > 10 && c <= 30)) ranges.push({ value: '11-30', label: '11–30', min: 11, max: 30 });
    if (counts.some(c => c > 30 && c <= 50)) ranges.push({ value: '31-50', label: '31–50', min: 31, max: 50 });
    if (counts.some(c => c > 50)) ranges.push({ value: '50+', label: '50+', min: 51, max: Infinity });
    return ranges;
  }, [models]);

  const filteredAndSorted = useMemo(() => {
    let result = [...models];
    if (typeFilter !== 'all') {
      result = result.filter(m => typeFilter === 'etf' ? m.model_type === 'etf' : m.model_type !== 'etf');
    }
    if (universeFilter !== 'all') {
      result = result.filter(m => getUniverse(m) === universeFilter);
    }
    if (holdingsFilter !== 'all') {
      const range = holdingsRanges.find(r => r.value === holdingsFilter);
      if (range) {
        result = result.filter(m => {
          const count = getHoldingsCount(m);
          return count >= range.min && count <= range.max;
        });
      }
    }
    result.sort((a, b) => {
      let aVal, bVal;
      if (sortKey === 'model_name') {
        aVal = (a.model_name || '').toLowerCase();
        bVal = (b.model_name || '').toLowerCase();
      } else if (sortKey === 'no_stocks') {
        aVal = getHoldingsCount(a);
        bVal = getHoldingsCount(b);
      } else {
        aVal = a[sortKey] ?? -Infinity;
        bVal = b[sortKey] ?? -Infinity;
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [models, typeFilter, universeFilter, holdingsFilter, holdingsRanges, sortKey, sortDir]);

  const activeFilterCount = (typeFilter !== 'all' ? 1 : 0) + (universeFilter !== 'all' ? 1 : 0) + (holdingsFilter !== 'all' ? 1 : 0);

  const clearFilters = () => {
    setTypeFilter('all');
    setUniverseFilter('all');
    setHoldingsFilter('all');
  };

  const handleRowClick = (model) => {
    navigate(`/model/${model.id}`, { state: { model } });
  };

  const handleExportPDF = async ({ whitelabel = false, extraText = '' } = {}) => {
    if (!filteredAndSorted || filteredAndSorted.length === 0) return;
    const pdfData = filteredAndSorted.map(model => [
      whitelabel ? getWhitelabelModelName(model.model_name) : (model.model_name || 'N/A'),
      getUniverse(model),
      getHoldingsCount(model) || 'N/A',
      model.ann_ret !== null && model.ann_ret !== undefined ? formatPDFPercentage(model.ann_ret, 2) : 'N/A',
      model.excess_ret !== null && model.excess_ret !== undefined ? formatPDFPercentage(model.excess_ret, 2) : 'N/A',
      model.sharpe !== null && model.sharpe !== undefined ? formatPDFNumber(model.sharpe, 2) : 'N/A',
      model.month_turnover !== null && model.month_turnover !== undefined ? formatPDFPercentage(model.month_turnover, 2) : 'N/A'
    ]);
    await generateDataTablePDF({
      title: 'Model Performance',
      subtitle: 'SharpeMetrix Model Portfolio Performance Metrics',
      columns: ['Model Name', 'Universe', 'Holdings', 'Annual Return', 'Excess Return', 'Sharpe Ratio', 'Monthly Turnover'],
      data: pdfData,
      filename: `${whitelabel ? 'Whitelabel' : 'SharpeMetrix'}_Models_${new Date().toISOString().split('T')[0]}.pdf`,
      date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
      whitelabel: whitelabel ? getWhitelabelSettings(userRecord) : null,
      whitelabelExtraText: extraText,
    });
  };

  const formatTableValue = (value, key) => {
    if (value === null || value === undefined) return <span className="text-text-300">—</span>;
    const numValue = parseFloat(value);
    let colorClass = 'text-text-100 font-medium';
    if (key === 'ann_ret' || key === 'excess_ret') {
      colorClass = numValue >= 0 ? 'text-emerald-400 font-medium' : 'text-rose-400 font-medium';
    }
    const formatted = key === 'sharpe' ? numValue.toFixed(2) : `${numValue.toFixed(1)}%`;
    return <span className={colorClass}>{formatted}</span>;
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="flex-grow px-4 sm:px-6 lg:px-8 py-12 bg-bg-100">
          <div className="mt-8 max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold tracking-tight text-text-100 sm:text-4xl">Model Portfolios</h2>
            <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse rounded-2xl bg-bg-200 h-72" />
              ))}
            </div>
          </div>
        </div>
        <div className="w-full bg-bg-200"><DisclaimerFooter small /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="flex-grow px-4 sm:px-6 lg:px-8 py-12 bg-bg-100">
          <div className="mt-8 max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold tracking-tight text-text-100 sm:text-4xl">Model Portfolios</h2>
            <div className="mt-8 flex justify-center items-center py-12">
              <div className="text-center">
                <svg className="mx-auto h-12 w-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="mt-4 text-text-100 font-semibold">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-100"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="w-full bg-bg-200"><DisclaimerFooter small /></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <SEO title="Model Portfolios" description="Browse institutional model portfolios ranked by the SharpeMetrix Power List™." />
      <div className="flex-grow px-4 sm:px-6 lg:px-8 py-6 sm:py-8 bg-bg-100">
        <div className="max-w-7xl mx-auto">

          {/* Quick access */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
            {QUICK_LINKS.map(({ label, description, href, icon: Icon, accent, border }) => (
              <button
                key={href}
                onClick={() => navigate(href)}
                className={`text-left rounded-xl bg-bg-200 border border-bg-300 ${border} px-4 py-3.5 transition-all group hover:bg-bg-300`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <Icon className={`w-4 h-4 ${accent} shrink-0`} />
                  <span className={`text-sm font-semibold ${accent}`}>{label}</span>
                </div>
                <p className="text-xs text-text-300 leading-relaxed">{description}</p>
              </button>
            ))}
          </div>

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-text-100">Model Portfolios</h2>
              <p className="mt-1 text-sm text-text-300">
                {filteredAndSorted.length} model{filteredAndSorted.length !== 1 ? 's' : ''}
                {activeFilterCount > 0 && <span> (filtered)</span>}
              </p>
            </div>
            <div className="flex items-center gap-2 self-start sm:self-auto">
              {/* View Toggle */}
              <div className="flex rounded-lg overflow-hidden border border-bg-300">
                <button
                  onClick={() => setViewMode('cards')}
                  className={`p-2 transition-colors ${
                    viewMode === 'cards'
                      ? 'bg-primary-100 text-white'
                      : 'bg-bg-200 text-text-300 hover:text-text-100'
                  }`}
                  title="Card view"
                >
                  <Squares2X2Icon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-2 transition-colors ${
                    viewMode === 'table'
                      ? 'bg-primary-100 text-white'
                      : 'bg-bg-200 text-text-300 hover:text-text-100'
                  }`}
                  title="Table view"
                >
                  <TableCellsIcon className="h-4 w-4" />
                </button>
              </div>
              <button
                onClick={() => setExportModalOpen(true)}
                disabled={models.length === 0}
                className="inline-flex items-center gap-1.5 rounded-lg bg-bg-200 px-3 py-2 text-sm font-medium text-text-200 hover:bg-bg-300 hover:text-text-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Export PDF</span>
              </button>
            </div>
          </div>

          {/* Filter Bar — always visible */}
          <div className="mb-6 rounded-xl bg-bg-200 border border-bg-300 p-3 sm:p-4">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4 items-end">
              {/* Type Filter */}
              <div>
                <label className="block text-xs font-medium text-text-300 mb-1.5">Type</label>
                <div className="flex rounded-lg overflow-hidden border border-bg-300">
                  {[
                    { value: 'all', label: 'All' },
                    { value: 'stock', label: 'Stock' },
                    { value: 'etf', label: 'ETF' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setTypeFilter(opt.value)}
                      className={`flex-1 px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium transition-colors ${
                        typeFilter === opt.value
                          ? 'bg-primary-100 text-white'
                          : 'bg-bg-200 text-text-300 hover:bg-bg-300 hover:text-text-100'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Universe Filter */}
              <div>
                <label className="block text-xs font-medium text-text-300 mb-1.5">Universe</label>
                <select
                  value={universeFilter}
                  onChange={(e) => setUniverseFilter(e.target.value)}
                  className="w-full rounded-lg border border-bg-300 bg-bg-100 px-3 py-2 text-xs sm:text-sm text-text-100 focus:border-primary-100 focus:ring-1 focus:ring-primary-100"
                >
                  <option value="all">All</option>
                  {universes.map(u => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>

              {/* Holdings Filter */}
              <div>
                <label className="block text-xs font-medium text-text-300 mb-1.5"># of Stocks</label>
                <select
                  value={holdingsFilter}
                  onChange={(e) => setHoldingsFilter(e.target.value)}
                  className="w-full rounded-lg border border-bg-300 bg-bg-100 px-3 py-2 text-xs sm:text-sm text-text-100 focus:border-primary-100 focus:ring-1 focus:ring-primary-100"
                >
                  <option value="all">All</option>
                  {holdingsRanges.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-xs font-medium text-text-300 mb-1.5">Sort by</label>
                <div className="flex gap-1.5">
                  <select
                    value={sortKey}
                    onChange={(e) => setSortKey(e.target.value)}
                    className="flex-1 min-w-0 rounded-lg border border-bg-300 bg-bg-100 px-3 py-2 text-xs sm:text-sm text-text-100 focus:border-primary-100 focus:ring-1 focus:ring-primary-100"
                  >
                    {SORT_OPTIONS.map(opt => (
                      <option key={opt.key} value={opt.key}>{opt.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')}
                    className="flex items-center justify-center rounded-lg border border-bg-300 bg-bg-100 px-2 text-text-300 hover:text-text-100 hover:border-primary-100 transition-colors"
                    title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
                  >
                    <ChevronUpDownIcon className="h-4 w-4" />
                    <span className="text-xs ml-0.5">{sortDir === 'asc' ? '↑' : '↓'}</span>
                  </button>
                </div>
              </div>

              {/* Clear */}
              <div className={activeFilterCount > 0 ? '' : 'invisible'}>
                <label className="block text-xs font-medium text-text-300 mb-1.5">&nbsp;</label>
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1 rounded-lg border border-bg-300 bg-bg-100 px-3 py-2 text-xs sm:text-sm font-medium text-text-300 hover:text-text-100 hover:border-primary-100 transition-colors w-full justify-center"
                >
                  <XMarkIcon className="h-4 w-4" />
                  Clear
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          {filteredAndSorted.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-text-300 text-sm">No models match your filters.</p>
              <button onClick={clearFilters} className="mt-3 text-primary-100 text-sm font-medium hover:underline">
                Clear filters
              </button>
            </div>
          ) : viewMode === 'cards' ? (
            /* Cards Grid */
            <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filteredAndSorted.map((model) => (
                <ModelCard key={model.id} model={model} onClick={() => handleRowClick(model)} />
              ))}
            </div>
          ) : (
            /* Table View */
            <div className="overflow-hidden rounded-lg border border-bg-300 shadow-lg">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-bg-300">
                  <thead className="bg-bg-200">
                    <tr>
                      <th scope="col" className="sticky top-0 bg-bg-200 px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider text-text-300">
                        Model
                      </th>
                      <th scope="col" className="sticky top-0 bg-bg-200 px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider text-text-300 hidden lg:table-cell">
                        Description
                      </th>
                      {TABLE_COLUMNS.map(col => (
                        <th
                          key={col.key}
                          scope="col"
                          className="sticky top-0 bg-bg-200 px-4 py-4 text-right text-xs font-semibold uppercase tracking-wider text-text-300 cursor-pointer hover:text-text-100 transition-colors select-none group"
                          onClick={() => {
                            if (sortKey === col.key) {
                              setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSortKey(col.key);
                              setSortDir('desc');
                            }
                          }}
                        >
                          <span className="inline-flex items-center gap-1.5">
                            {col.label}
                            <ArrowsUpDownIcon className={`h-3.5 w-3.5 transition-colors ${sortKey === col.key ? 'text-primary-100' : 'text-text-300 group-hover:text-text-200'}`} />
                            {sortKey === col.key && (
                              <span className="text-primary-100 text-sm">{sortDir === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-bg-200 divide-y divide-bg-300">
                    {filteredAndSorted.map((model) => (
                      <tr
                        key={model.id}
                        onClick={() => handleRowClick(model)}
                        className="cursor-pointer hover:bg-bg-300/50 transition-colors"
                      >
                        <td className="px-4 py-5 whitespace-nowrap">
                          <div className="flex items-center gap-2.5">
                            <span className="text-sm font-semibold text-text-100">
                              {model.model_name}
                            </span>
                            {model.model_type === 'etf' && (
                              <span className="inline-flex items-center rounded-md bg-purple-400/10 px-2 py-1 text-xs font-medium text-purple-400 border border-purple-400/20">
                                ETF
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-5 hidden lg:table-cell">
                          <p className="text-sm text-text-300 line-clamp-2 max-w-2xl leading-relaxed">
                            {model.model_description}
                          </p>
                        </td>
                        {TABLE_COLUMNS.map(col => (
                          <td key={col.key} className="px-4 py-5 whitespace-nowrap text-right text-sm tabular-nums">
                            {formatTableValue(model[col.key], col.key)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Footer note */}
          {filteredAndSorted.length > 0 && (
            <p className="mt-6 text-center text-xs text-text-300">
              Performance since inception &middot; Click any {viewMode === 'cards' ? 'card' : 'row'} for details
            </p>
          )}
        </div>
      </div>
      <PdfExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        onExport={handleExportPDF}
        whitelabelSettings={getWhitelabelSettings(userRecord)}
        title="Export Model List PDF"
      />
      <div className="w-full bg-bg-200">
        <DisclaimerFooter small />
      </div>
    </div>
  );
}

function ModelCard({ model, onClick }) {
  const isETF = model.model_type === 'etf';
  const holdings = getHoldingsCount(model);
  const universe = getUniverse(model);

  const formatReturn = (val) => {
    if (val === null || val === undefined) return '—';
    return `${parseFloat(val).toFixed(1)}%`;
  };

  const formatSharpe = (val) => {
    if (val === null || val === undefined) return '—';
    return parseFloat(val).toFixed(2);
  };

  const annRet = parseFloat(model.ann_ret);
  const isPositive = !isNaN(annRet) && annRet >= 0;

  return (
    <button
      onClick={onClick}
      className="group text-left w-full rounded-2xl bg-bg-200 border-2 border-bg-300 overflow-hidden hover:border-primary-100/60 hover:shadow-lg hover:shadow-primary-100/5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-100 focus:ring-offset-2 focus:ring-offset-bg-100"
    >
      <div className="p-5 sm:p-6">
        {/* Header: Name + Badge */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="text-base sm:text-lg font-semibold text-text-100 group-hover:text-primary-100 transition-colors leading-tight">
            {model.model_name}
          </h3>
          <span className={`shrink-0 inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${
            isETF
              ? 'bg-purple-400/10 text-purple-400 ring-purple-400/20'
              : 'bg-blue-400/10 text-blue-400 ring-blue-400/20'
          }`}>
            {isETF ? 'ETF' : 'Stock'}
          </span>
        </div>

        {/* Description */}
        {model.model_description && (
          <p className="text-sm text-text-300 leading-relaxed line-clamp-2 mb-4">
            {model.model_description}
          </p>
        )}

        {/* Universe tag */}
        <div className="mb-4">
          <span className="inline-flex items-center rounded-full bg-bg-300/50 px-2.5 py-0.5 text-xs text-text-300">
            {universe}
          </span>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <div className="rounded-lg bg-bg-100 p-2.5 sm:p-3 text-center">
            <p className={`text-lg sm:text-xl font-bold tabular-nums ${isPositive ? 'text-emerald-400' : 'text-rose-400'}`}>
              {formatReturn(model.ann_ret)}
            </p>
            <p className="text-[10px] sm:text-xs text-text-300 mt-0.5">Annual Return</p>
          </div>
          <div className="rounded-lg bg-bg-100 p-2.5 sm:p-3 text-center">
            <p className="text-lg sm:text-xl font-bold text-primary-100 tabular-nums">
              {holdings || '—'}
            </p>
            <p className="text-[10px] sm:text-xs text-text-300 mt-0.5">Holdings</p>
          </div>
          <div className="rounded-lg bg-bg-100 p-2.5 sm:p-3 text-center">
            <p className="text-lg sm:text-xl font-bold text-text-100 tabular-nums">
              {formatSharpe(model.sharpe)}
            </p>
            <p className="text-[10px] sm:text-xs text-text-300 mt-0.5">Sharpe Ratio</p>
          </div>
        </div>

        {/* Turnover */}
        <div className="mt-3 flex items-center justify-end text-xs text-text-300 border-t border-bg-300/50 pt-3">
          <span>
            Turnover: <span className="font-medium text-text-200">
              {model.month_turnover !== null && model.month_turnover !== undefined
                ? `${parseFloat(model.month_turnover).toFixed(1)}%/mo`
                : '—'
              }
            </span>
          </span>
        </div>
      </div>
    </button>
  );
}
